import * as React from 'react';
import { Link, LinkProps, useLocation } from 'react-router-dom';

import CollectionsIcon from '@mui/icons-material/Collections';
import MuiBadge from '@mui/material/Badge';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import Tab, { TabProps, TabTypeMap } from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';

import { useConversationsContext } from '../../../contexts/ConversationsContext';
import { useNotificationsContext } from '../../../contexts/NotificationsContext';
import { useOnMessagingBtwVenueAndUserEventListener } from '../../../hooks/listeners/useOnMessagingBtwVenueAndUserEventListener';
import useVenueId from '../../../hooks/useVenueId';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { ChatIcon, CollabsIcon, HomeIcon, KeyIcon, OffersIcon, ReservationIcon } from '../../Icons';

type Menu = { path: string; matched: string; icon: JSX.Element; label: string };

const menus = (isOnlineBrand = false): Menu[] => {
  const dashboard: Menu = { path: '', matched: 'NA', icon: <HomeIcon />, label: 'Dashboard' };
  const collabs: Menu = {
    path: 'collabs',
    matched: '/collabs',
    icon: <CollabsIcon />,
    label: 'Paid Collabs',
  };
  const accessRequests: Menu = {
    path: 'access-requests',
    matched: '/access-requests',
    icon: <KeyIcon />,
    label: isOnlineBrand ? 'Product Requests' : 'Access',
  };
  const reservations: Menu = {
    path: 'reservations',
    matched: '/reservations',
    icon: <ReservationIcon sx={{ fontSize: 26 }} aria-label='reservations' />,
    label: 'Reservations',
  };
  const content: Menu = {
    path: 'content',
    matched: '/content',
    icon: <CollectionsIcon />,
    label: 'Content',
  };
  const offers: Menu = {
    path: isOnlineBrand ? 'products' : 'offers',
    matched: isOnlineBrand ? '/products' : '/offers',
    icon: <OffersIcon />,
    label: isOnlineBrand ? 'Products' : 'Offers',
  };
  const chats: Menu = { path: 'chats', matched: '/chats', icon: <ChatIcon />, label: 'Chats' };

  if (isOnlineBrand) {
    return [dashboard, offers, accessRequests, reservations, content, chats, collabs];
  }

  return [dashboard, offers, reservations, content, accessRequests, chats, collabs];
};

type LinkTabProps = TabProps<typeof Link, LinkProps>;
type LinkTabType = OverridableComponent<TabTypeMap<LinkTabProps>>;

const a11yProps = (index: number) => ({
  id: `vertical-tab-${index}`,
  'aria-controls': `vertical-tabpanel-${index}`,
});

const StyledTabs = styled(MuiTabs)(({ theme }) => ({
  '.MuiButtonBase-root.Mui-selected': { color: theme.custom.colors.brandPurple },
  '.MuiTabs-indicator': { left: 0, width: 4, backgroundColor: theme.custom.colors.brandPurple },
}));

const Badge = styled(MuiBadge)(({ theme }) => ({
  '.MuiBadge-badge': {
    right: theme.spacing(4),
    background:
      'transparent linear-gradient(25deg, #FFD47B 0%, #FF778E 46%, #B97AFF 100%) 0% 0% no-repeat padding-box',
  },
}));

const StyledTab = styled(Tab)<LinkTabProps>(({ theme }) => ({
  fontSize: '1rem',
  justifyContent: 'flex-start',
  minHeight: theme.spacing(6),
  '.MuiSvgIcon-root': {
    marginRight: theme.spacing(4),
    marginLeft: theme.spacing(0.5),
    ':not([aria-label="reservation"])': {
      fontSize: 24,
    },
  },
})) as LinkTabType;

const LinkTab: React.FC<LinkTabProps> = (props) => <StyledTab component={Link} {...props} />;

const Tabs: React.FC = () => {
  const venueID = useVenueId();
  const venue = useAppSelector((state) => state.venue.value);
  const [badges, setBadges] = React.useState<string[]>(['/chats']);

  const { data: conversations, revalidateConversations } = useConversationsContext() || {};
  const {
    reservation,
    lastReservationCreatedAtTime,
    invite,
    lastCollabUpdatedAtTime,
    content,
    lastContentUpdatedAtTime,
    lastAccessRequestUpdatedAtTime,
    accessRequest,
  } = useNotificationsContext() || {};
  const { message } = useOnMessagingBtwVenueAndUserEventListener(venueID || '');

  const [unreadMessages, setUnreadMessages] = React.useState(0);

  React.useEffect(() => {
    if (conversations) {
      const totalUnreadMessages = conversations.reduce(
        (acc, item) => (acc = acc + (item?.fromUserMessageCount || 0)),
        0,
      );
      setUnreadMessages(totalUnreadMessages || 0);
    }
  }, [conversations]);

  React.useEffect(() => {
    if (message) {
      setUnreadMessages((prev) => prev + 1);

      if (revalidateConversations) {
        revalidateConversations();
      }
    }
  }, [message]);

  const [value, setValue] = React.useState<number | false>(0);
  const { pathname } = useLocation();

  const handleChange = React.useCallback(
    (_: React.SyntheticEvent, newValue: number) => setValue(newValue),
    [],
  );

  React.useEffect(() => {
    if (pathname !== '/') {
      setBadges((prev) => {
        const _badges = prev.filter((badge) => !badge.includes(pathname));
        return _badges;
      });
    }
  }, [pathname]);

  React.useEffect(() => {
    const i =
      pathname !== '/'
        ? menus(venue?.tags?.includes(VENUE_MANUAL_TAGS.Online)).findIndex((x) =>
            pathname.startsWith(x.matched),
          )
        : 0;

    if (value !== i) {
      setValue(i > 0 ? i : false);
    }
  }, [value, pathname, venue?.tags]);

  React.useEffect(() => {
    const b = [];

    if (
      accessRequest &&
      lastAccessRequestUpdatedAtTime &&
      accessRequest?.createdAt !== lastAccessRequestUpdatedAtTime
    ) {
      b.push('/access-requests');
    }

    if (invite && lastCollabUpdatedAtTime && invite?.updatedAt !== lastCollabUpdatedAtTime) {
      b.push('/collabs');
    }

    if (
      reservation &&
      lastReservationCreatedAtTime &&
      reservation?.createdAt !== lastReservationCreatedAtTime
    ) {
      b.push('/reservations');
    }

    if (unreadMessages > 0) {
      b.push('/chats');
    }

    if (content && content?.createdAt !== lastContentUpdatedAtTime) {
      b.push('/content');
    }

    setBadges(b);
  }, [
    invite,
    lastCollabUpdatedAtTime,
    lastReservationCreatedAtTime,
    reservation,
    unreadMessages,
    content,
    lastContentUpdatedAtTime,
    accessRequest,
    lastAccessRequestUpdatedAtTime,
  ]);

  return (
    <StyledTabs
      value={value}
      onChange={handleChange}
      orientation='vertical'
      aria-label='Vertical tabs'
    >
      {menus(venue?.tags?.includes(VENUE_MANUAL_TAGS.Online)).map((x, i) => (
        <LinkTab
          key={i}
          wrapped
          icon={
            <Badge color='primary' variant='dot' invisible={!badges.find((y) => y === x.matched)}>
              {x.icon}
            </Badge>
          }
          iconPosition='start'
          label={x.label}
          to={x.path}
          {...a11yProps(i)}
        />
      ))}
    </StyledTabs>
  );
};

export default Tabs;
